<template>
  <div class="container-fluid op-wrapper">
    <h1 class="text-center op-title">Operator Identifier</h1>
    <p class="text-center op-info">
      Which operator makes the expression correct? Fill in the <strong>?</strong>
    </p>

    <!-- Question card -->
    <transition name="flip" mode="out-in">
      <div class="question-card" :key="round">
        <div class="expression">
          <span class="num">{{ a }}</span>
          <span class="op-blank">?</span>
          <span class="num">{{ b }}</span>
          <span class="eq">=</span>
          <span class="num">{{ result }}</span>
        </div>

        <!-- Feedback -->
        <transition name="fade">
          <div
            v-if="feedback"
            class="feedback"
            :class="feedbackClass"
          >
            {{ feedback }}
          </div>
        </transition>

        <!-- Operator buttons -->
        <div class="op-buttons" v-if="!answered">
          <button
            v-for="op in operators"
            :key="op.symbol"
            class="btn op-btn"
            @click="pick(op)"
          >
            {{ op.symbol }}
          </button>
        </div>

        <!-- Next button -->
        <div class="text-center" v-if="answered">
          <button class="btn next-btn" @click="newQuestion">
            Next Question
          </button>
        </div>
      </div>
    </transition>

    <!-- Operator reference legend -->
    <div class="legend">
      <h3 class="legend-title">Quick Reference</h3>
      <div class="legend-grid">
        <div class="legend-item" v-for="op in operators" :key="op.symbol">
          <span class="legend-symbol">{{ op.symbol }}</span>
          <span class="legend-name">{{ op.name }}</span>
          <span class="legend-desc">{{ op.description }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";

const OPERATORS = [
  {
    symbol: "+",
    name: "Addition",
    description: "Adds two numbers together",
    apply: (a, b) => a + b,
  },
  {
    symbol: "−",
    name: "Subtraction",
    description: "Subtracts the second from the first",
    apply: (a, b) => a - b,
  },
  {
    symbol: "×",
    name: "Multiplication",
    description: "Multiplies two numbers together",
    apply: (a, b) => a * b,
  },
  {
    symbol: "÷",
    name: "Division",
    description: "Divides the first number by the second",
    apply: (a, b) => a / b,
  },
];

// Streak milestones for swal celebrations
const STREAK_MILESTONES = [3, 5, 10, 15, 20];

export default {
  data() {
    return {
      operators: OPERATORS,
      a: 0,
      b: 0,
      result: 0,
      correctOp: null,
      answered: false,
      feedback: "",
      feedbackClass: "",
      score: 0,
      streak: 0,
      round: 1,
    };
  },
  methods: {
    generateQuestion() {
      // Pick a random operator
      const op = OPERATORS[Math.floor(Math.random() * OPERATORS.length)];
      this.correctOp = op;

      let a, b, result;

      // Generate clean numbers based on operator to avoid messy decimals
      if (op.symbol === "+") {
        a = Math.floor(Math.random() * 99) + 1;
        b = Math.floor(Math.random() * 99) + 1;
        result = op.apply(a, b);
      } else if (op.symbol === "−") {
        a = Math.floor(Math.random() * 99) + 10;
        b = Math.floor(Math.random() * (a - 1)) + 1; // ensure positive result
        result = op.apply(a, b);
      } else if (op.symbol === "×") {
        a = Math.floor(Math.random() * 12) + 1;
        b = Math.floor(Math.random() * 12) + 1;
        result = op.apply(a, b);
      } else {
        // Division - pick divisor then multiply to guarantee whole number
        b = Math.floor(Math.random() * 11) + 2;       // divisor 2–12
        result = Math.floor(Math.random() * 12) + 1;   // quotient 1–12
        a = b * result;
      }

      this.a = a;
      this.b = b;
      this.result = result;
    },

    pick(op) {
      if (this.answered) return;
      this.answered = true;

      if (op.symbol === this.correctOp.symbol) {
        this.score += 10;
        this.streak += 1;
        this.showFeedback(`✓ Correct! It's ${op.name} (${op.symbol})`, "feedback-correct");

        if (STREAK_MILESTONES.includes(this.streak)) {
          swal(`${this.streak} in a row! Keep it up! 🔥`, {
            closeOnClickOutside: false,
          });
        }
      } else {
        this.streak = 0;
        this.showFeedback(
          `✗ Wrong! The answer is ${this.correctOp.name} (${this.correctOp.symbol})`,
          "feedback-wrong"
        );
      }
    },

    newQuestion() {
      this.answered = false;
      this.feedback = "";
      this.round += 1;
      this.generateQuestion();
    },

    showFeedback(msg, cls) {
      this.feedback = msg;
      this.feedbackClass = cls;
    },
  },
  created() {
    this.generateQuestion();
  },
};
</script>

<style scoped>
.op-wrapper {
  padding: 20px 16px 60px;
  min-height: 100vh;
}

.op-title {
  color: rgb(3, 53, 48);
  margin-bottom: 6px;
}

.op-info {
  font-size: 17px;
  margin-bottom: 16px;
}

/* Question card */
.question-card {
  max-width: 500px;
  margin: 0 auto 32px;
  border: 2px dashed rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  padding: 28px 20px 24px;
  background: rgba(169, 247, 234, 0.15);
  text-align: center;
}

/* Expression display */
.expression {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.num {
  font-size: 42px;
  font-weight: bold;
  color: rgb(3, 53, 48);
}

.op-blank {
  font-size: 48px;
  font-weight: bold;
  color: rgb(180, 30, 30);
  min-width: 42px;
  border-bottom: 3px solid rgb(180, 30, 30);
  line-height: 1;
}

.eq {
  font-size: 42px;
  font-weight: bold;
  color: rgb(3, 53, 48);
}

/* Feedback */
.feedback {
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 6px;
}

.feedback-correct {
  background: rgba(3, 120, 60, 0.1);
  color: rgb(3, 100, 50);
}

.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
}

/* Operator choice buttons */
.op-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.op-btn {
  width: 72px;
  height: 72px;
  font-size: 32px;
  font-weight: bold;
  border-radius: 10px;
  background-color: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  border: 2px solid rgb(3, 53, 48);
  cursor: pointer;
  transition: transform 0.15s, background-color 0.2s;
}

.op-btn:hover {
  background-color: rgb(3, 53, 48);
  color: white;
  transform: scale(1.1);
}

/* Next button */
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 36px;
  border-radius: 6px;
  font-size: 16px;
  border: none;
  cursor: pointer;
  margin-top: 8px;
}

.next-btn:hover {
  background-color: rgb(5, 80, 70);
}

/* Legend / reference */
.legend {
  max-width: 600px;
  margin: 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.5);
}

.legend-title {
  text-align: center;
  font-size: 20px;
  margin-bottom: 14px;
  color: rgb(3, 53, 48);
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 14px;
}

.legend-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 8px;
  border-radius: 8px;
  background: rgb(169, 247, 234);
  border: 1px solid rgba(3, 53, 48, 0.2);
}

.legend-symbol {
  font-size: 30px;
  font-weight: bold;
  color: rgb(3, 53, 48);
  line-height: 1;
  margin-bottom: 4px;
}

.legend-name {
  font-size: 14px;
  font-weight: bold;
  color: rgb(3, 53, 48);
}

.legend-desc {
  font-size: 12px;
  color: rgba(3, 53, 48, 0.75);
  margin-top: 4px;
}

/* Animations */
.flip-enter-active { animation: flip-in 0.4s ease-out forwards; }
.flip-leave-active { animation: flip-out 0.3s ease-out forwards; }

@keyframes flip-out {
  from { transform: rotateY(0deg);  opacity: 1; }
  to   { transform: rotateY(90deg); opacity: 0; }
}
@keyframes flip-in {
  from { transform: rotateY(90deg); opacity: 0; }
  to   { transform: rotateY(0deg);  opacity: 1; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>
